"use client";

import React, { createContext, useContext, useState, useMemo } from "react";

export type TimeRangePreset = "7d" | "30d" | "90d" | "custom";

export interface TimeRange {
  preset: TimeRangePreset;
  startDate?: Date;
  endDate?: Date;
}

interface TimeRangeContextType {
  timeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
  getDateRange: () => { start: Date; end: Date };
}

const TimeRangeContext = createContext<TimeRangeContextType | undefined>(
  undefined
);

const STORAGE_KEY = "frms-time-range";

export function TimeRangeProvider({
  children
}: Readonly<{ children: React.ReactNode }>) {
  const [timeRange, setTimeRangeState] = useState<TimeRange>(() => {
    // Try to load from localStorage
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          // Convert date strings back to Date objects
          if (parsed.startDate) parsed.startDate = new Date(parsed.startDate);
          if (parsed.endDate) parsed.endDate = new Date(parsed.endDate);
          return parsed;
        } catch (error) {
          console.error("Failed to parse stored time range:", error);
          // Use default values on parse error
        }
      }
    }
    // Default to last 7 days
    return { preset: "7d" };
  });

  const setTimeRange = (range: TimeRange) => {
    setTimeRangeState(range);
    // Persist to localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(range));
    }
  };

  const getDateRange = (): { start: Date; end: Date } => {
    const now = new Date();

    if (
      timeRange.preset === "custom" &&
      timeRange.startDate &&
      timeRange.endDate
    ) {
      return { start: timeRange.startDate, end: timeRange.endDate };
    }

    let daysBack: number;
    switch (timeRange.preset) {
      case "7d":
        daysBack = 7;
        break;
      case "30d":
        daysBack = 30;
        break;
      case "90d":
        daysBack = 90;
        break;
      default:
        daysBack = 7;
    }

    const start = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
    return { start, end: now };
  };

  const contextValue = useMemo(
    () => ({ timeRange, setTimeRange, getDateRange }),
    [timeRange]
  );

  return (
    <TimeRangeContext.Provider value={contextValue}>
      {children}
    </TimeRangeContext.Provider>
  );
}

export function useTimeRange() {
  const context = useContext(TimeRangeContext);
  if (!context) {
    throw new Error("useTimeRange must be used within a TimeRangeProvider");
  }
  return context;
}
